import Vue from 'vue'

// Sentry — Error Monitoring (VSUS-885)
import * as Sentry from '@sentry/vue'
import router from './router'

if (process.env.VUE_APP_SENTRY_DSN && process.env.NODE_ENV === 'production') {
  try {
    Sentry.init({
      Vue,
      dsn: process.env.VUE_APP_SENTRY_DSN,
      environment: process.env.VUE_APP_SENTRY_ENV || 'development',
      release: process.env.VUE_APP_SENTRY_RELEASE || 'local',

      sampleRate: 0.1,
      tracesSampleRate: 0.05,
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 0.1,

      integrations: [
        Sentry.browserTracingIntegration({ router }),
        Sentry.replayIntegration({
          maskAllText: false,
          blockAllMedia: false,
        }),
      ],

      ignoreErrors: [
        'ResizeObserver loop',
        'Non-Error exception captured',
        'Non-Error promise rejection captured',
        // Rede do usuário — fora do nosso controle
        // FIX: axios lança "Network Error" (com espaço) — antes só tinha 'NetworkError' colado
        // Evidência: VOOMP-ADM-6 (69 eventos, 64 usuários) chegando ao Sentry sem parar
        'Network Error',
        'NetworkError',
        'Failed to fetch',
        'Load failed',
        'Network request failed',
        'AbortError',
        'TypeError: cancelled',
        'TypeError: Cancelled',
        // VSUS-1474: chunkId é "chunk-<hex>", não só dígitos
        /Loading chunk [\w.-]+ failed/i,
        /Loading CSS chunk [\w.-]+ failed/i,
        'Importing a module script failed',
        'ChunkLoadError',
        'top.GLOBALS',
        'originalCreateNotification',
        'canvas.contentDocument',
        'vid_mate_check is not defined',
        'atomicFindClose',
        'conduitPage',
        'ibFindAllVideos',
        'feedConf',
        'myGloFrameList',
        /__show__deepen/,
        /_avast_submit/,
        /change_ua/,
        'fb_xd_fragment',
        'bmi_SafeAddOnload',
        'EBCallBackMessageReceived',
        /ztePageScrollModule/,
        'Script error.',
        'SecurityError',
        'Access is denied',
        'Blocked a frame with origin',
        /SecurityError: DOM Exception 18/,
        /.*@webkit-masked-url.*/,
        "Can't find variable: _AutofillCallbackHandler",
        "Can't execute code from a freed script",
        // Akamai Bot Manager (AdBlock pode bloquear o script)
        'bmak is not defined',
        "Can't find variable: bmak",
        // reCAPTCHA / hCaptcha (scripts externos podem falhar)
        'recaptcha',
        'grecaptcha is not defined',
        'hcaptcha',
        // VeeValidate 2.x race condition (componente destruído com validação pendente)
        // FIX: 'Object Not Found Matching Id' não cobria o padrão real do vee-validate
        // Evidência: VOOMP-ADM-12 — mensagem exata: "[vee-validate] Validating a non-existent field: "#30". Use "attach()" first."
        // O número (#30, #26, etc.) é dinâmico, o padrão é estável.
        'Object Not Found Matching Id',
        /\[vee-validate\] Validating a non-existent field:.*Use "attach\(\)" first/,
        // Payment SDKs (erros internos, não confundir com pagamento recusado)
        'paypal is not defined',
        "Can't find variable: paypal",
        // Vue Router (falhas de navegação esperadas — warning, não erro real)
        'NavigationDuplicated',
        'Avoided redundant navigation',
        /^Navigation cancelled/,
        /^Navigation aborted/,
        // vue-the-mask: setSelectionRange em input[type=number] — bug interno do SDK
        // Evidência: VOOMP-ADM-7 (8 eventos, 7 usuários)
        /Failed to execute 'setSelectionRange'/,
        // Mobile browsers que injetam JS (público BR)
        /zaloJSV2/,
        /instantSearchSDKJSBridgeClear/,
        /vivoNews/,
        // Toolbars e extensões legadas
        'jigsaw is not defined',
        'ComboSearch is not defined',
        'MyApp_RemoveAllHighlights',
        'WOW is not defined',
        '$ is not defined',
        'jQuery is not defined',
        // Axios timeout/cancel (rede instável do user, não é bug nosso)
        'timeout of',
        'Request aborted',
        'canceled',
        'Request failed with status code 401',
        'Request failed with status code 403',
        // Bootstrap-Vue tooltips/popovers (dispose em componente já destruído)
        "Cannot read properties of null (reading 'removeChild')",
        "Cannot read properties of null (reading 'emitEvent')",
        // Vue 2 warn que escapa como erro
        /\[Vue warn\]/,
        // Clipboard API (fallback, nem todo browser suporta)
        'document.execCommand',
        'ClipboardItem is not defined',
        // Stripe SDK (erros internos do SDK externo)
        /StripeTerminal/,
        'Stripe is not defined',
        // Meta Pixel / pixels de terceiros — fora do nosso controle
        /^Meta Pixel:/,
        /^Facebook Pixel/,
        /fbq is not defined/,
        // screen.orientation.lock — API não suportada em iOS/desktop (VOOMP-ADM-C)
        /screen\.orientation\.lock/,
        /NotSupportedError.*screen\.orientation/,
        // Redirected navigation (comportamento normal do vue-router)
        /^Redirected when going from/,
        // webkit.messageHandlers (WKWebView iOS — third party)
        /window\.webkit\.messageHandlers/,
        /webkit\.messageHandlers/,
        // Amplitude / analytics SDKs externos
        /e\.checkEvaluation is not a function/,
        /checkEvaluation/,
      ],

      denyUrls: [
        /extensions\//i,
        /^chrome:\/\//i,
        /^chrome-extension:\/\//i,
        /^moz-extension:\/\//i,
        /^safari-extension:\/\//i,
        /^resource:\/\//i,
        /\/(gtm|ga|analytics)\.js/i,
        /graph\.facebook\.com/i,
        /connect\.facebook\.net/i,
        /pagead\/js/i,
        /127\.0\.0\.1:4001\/isrunning/i,
        /webappstoolbarba\.texthelp\.com/i,
        /metrics\.itunes\.apple\.com/i,
        // Trackers e analytics externos
        /hotjar\.com/i,
        /clarity\.ms/i,
        /doubleclick\.net/i,
        // hCaptcha CDN (erros no SDK externo)
        /hcaptcha\.com/i,
        // Akamai bot manager
        /akam\//i,
        /edgekey\.net/i,
        // AppDynamics / adrum (SDK de monitoramento externo — VOOMP-ADM-5)
        /adrum\//i,
        /adrum-/i,
        // Datadog RUM SDK (erros internos — VOOMP-ADM-1J, 4 eventos)
        // Culprit: XMLHttpRequest.u(us1/v6/datadog-rum)
        /us1\/v\d+\/datadog-rum/i,
        /browser-sdk\.datadoghq\.com/i,
      ],

      beforeSend(event) {
        if (event.exception && event.exception.values) {
          const err = event.exception.values[0];
          const frames = err?.stacktrace?.frames || [];

          // Descarta erros de extensões de browser
          const isExtension = frames.some(
            (frame) => frame.filename && (
              frame.filename.includes('extension://') ||
              frame.filename.includes('internal://')
            )
          );
          if (isExtension) return null;

          // Fingerprinting: agrupa TypeErrors com mesma mensagem + arquivo
          // Sem source maps, o mesmo bug gera N issues diferentes por linha do bundle
          if (err?.type === 'TypeError' && err?.value && frames.length > 0) {
            const lastFrame = frames[frames.length - 1];
            if (lastFrame?.filename) {
              // Remove hash do bundle (ex: app.a1b2c3d4.js → app.js)
              const cleanFile = lastFrame.filename.replace(/\.[a-f0-9]{6,8}\.js/, '.js');
              event.fingerprint = [
                'TypeError',
                err.value.substring(0, 100),
                cleanFile,
              ];
            }
          }
        }
        return event;
      },
    });
  } catch (e) {
    console.warn('[Sentry] Failed to initialize:', e);
  }
}

import App from './App.vue'
import i18n from './i18n'
import BootstrapVue from 'bootstrap-vue';
import VueSweetalert2 from 'vue-sweetalert2'
import './assets/scss/multiple-select.scss'
import './assets/scss/sweet-container.scss'
import {
  library
} from '@fortawesome/fontawesome-svg-core';
import {
  fas
} from '@fortawesome/free-solid-svg-icons';
import {
  fab
} from '@fortawesome/free-brands-svg-icons';
import {
  FontAwesomeIcon
} from '@fortawesome/vue-fontawesome';
import VeeValidate from 'vee-validate';
import {
  Validator
} from 'vee-validate';
import pt_BR from 'vee-validate/dist/locale/pt_BR';
import store from './store';
import {
  date,
  datetime
} from './filters/date';

import VueGtm from "@gtm-support/vue2-gtm";

import { getGtmConfig } from "./utils/gtm";

import 'bootstrap/dist/css/bootstrap.css';
import 'bootstrap-vue/dist/bootstrap-vue.css';
import 'vue2-daterange-picker/dist/vue2-daterange-picker.css'
import VueTheMask from 'vue-the-mask';
import validate_cpf_cnpj from './validators/cpf_cnpj';
import validate_cpf from './validators/cpf';
import validate_cnpj from './validators/cnpj';
import components from './components/index'
import ModalValidation from '@/components/Client/Identity/ModalValidation'
import 'vue-phone-number-input/dist/vue-phone-number-input.css';

// editor MarkDown
import VueMarkdownEditor from '@kangc/v-md-editor';
import '@kangc/v-md-editor/lib/style/base-editor.css';
import hljs_editor from 'highlight.js/lib/core';

// Preview MarkDown
import VMdPreview from '@kangc/v-md-editor/lib/preview';
import '@kangc/v-md-editor/lib/style/preview.css';
import hljs_preview from 'highlight.js';

// Estilo MarkDown
import githubTheme from '@kangc/v-md-editor/lib/theme/github.js';
import '@kangc/v-md-editor/lib/theme/style/github.css';
import enUS from '@kangc/v-md-editor/lib/lang/en-US';
import ptBR from '@/assets/lang/pt-BR.js';
import json from 'highlight.js/lib/languages/json'

import CallEvaluation from './views/Seller/CallEvaluation'
import VueObserveVisibility from 'vue-observe-visibility'
import './directives/ClickOutsideDirective'

// XSS
import DOMPurify from 'dompurify';
Vue.prototype.$sanitize = DOMPurify.sanitize;

Vue.use(VueObserveVisibility)
Vue.component('CallEvaluation', CallEvaluation)


export const EventBus = new Vue();

for (let componentKey in components) {
  Vue.component(componentKey, components[componentKey])
}

import VueProgrammaticInvisibleGoogleRecaptcha from 'vue-programmatic-invisible-google-recaptcha/src/index.js'
Vue.component('vue-programmatic-invisible-google-recaptcha', VueProgrammaticInvisibleGoogleRecaptcha)

import ModalGroupAccept from '@/components/Seller/Settings/ModalGroupAccept.vue'
import { initializeAnalytics } from './utils/analytics.js';
import { initializeAmplitude } from './utils/amplitude.js';

Vue.component('modal-group-accept', ModalGroupAccept)

Vue.use(VueTheMask)
library.add(fas);
library.add(fab);
Vue.use(VeeValidate)
Validator.localize('pt_BR', pt_BR);
Validator.extend('cpf_cnpj', validate_cpf_cnpj);
Validator.extend('cpf', validate_cpf);
Validator.extend('cnpj', validate_cnpj);


Vue.component('font-awesome-icon', FontAwesomeIcon);
Vue.component('ModalValidation', ModalValidation);

Vue.use(VueGtm, getGtmConfig(router));
Vue.use(BootstrapVue);
Vue.use(VueSweetalert2);

Vue.filter('date', date);
Vue.filter('datetime', datetime);


// Config MarkDown
VueMarkdownEditor.lang.use('en-US', enUS);
VueMarkdownEditor.lang.add({
  'en-US': ptBR
})
hljs_editor.registerLanguage('json', json);
VueMarkdownEditor.use(githubTheme, {
  Hljs: hljs_editor,
});

VMdPreview.use(githubTheme, {
  Hljs: hljs_preview,
});

Vue.use(VueMarkdownEditor);
Vue.use(VMdPreview);
// Config MarkDown

Vue.use(VueGtm, {
  id: process.env.GTM_IDS,
  defer: false,
  enabled: true,
  debug: false,
  loadScript: true,
  vueRouter: router,
  trackOnNextTick: false,
})

Vue.config.productionTip = false;

router.beforeEach((to, from, next) => {
  const menu = document.querySelector(".Menu");
  if (menu) menu.classList.add("logo-close");
  next();
});

initializeAnalytics(Vue, router);
initializeAmplitude();

new Vue({
  router,
  store,
  i18n,
  render: h => h(App),
}).$mount('#app')
